import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { stackCategories, phases } from '../data/stack';
import StackGraphNode from './StackGraphNode';
import StackGraphSettings from './StackGraphSettings';
import StackGraphZoomControls from './StackGraphZoomControls';
import './StackGraph.css';

const allTools = stackCategories.flatMap((category) => category.tools);
const phaseIds = phases.map((phase) => phase.id);

function expandPhases(tool) {
  const tokens = (tool.phase || '').split(/\s+/).filter(Boolean);
  return tokens.includes('all') ? phaseIds : tokens;
}

const nodeSeeds = allTools.map((tool) => ({ tool, phases: expandPhases(tool) }));

const edgeSeeds = [];
for (let i = 0; i < nodeSeeds.length; i++) {
  for (let j = i + 1; j < nodeSeeds.length; j++) {
    if (nodeSeeds[i].phases.some((p) => nodeSeeds[j].phases.includes(p))) {
      edgeSeeds.push({ a: i, b: j });
    }
  }
}

const MIN_DIST = 30;
const CENTER_STRENGTH = 0.004;
// 0.8 loses 20% of velocity every frame — from MAX_SPEED that decays below
// VELOCITY_FLOOR in well under half a second, which reads as an abrupt halt
// rather than a float. Raised per explicit request to make released nodes
// "float around for a bit" instead of stopping dead — but not all the way
// to what felt best for a *single* released node in isolation (~0.94): with
// 19 nodes all settling together on initial page load (much more total
// system energy than one node coasting to rest), 0.94 stretched that whole
// settle out to ~8s, hitting MAX_RUN_MS's cutoff instead of resolving via
// the SETTLE_FRAMES low-energy check. 0.9 is the compromise — noticeably
// floatier single-node releases (~1s of drift) without dragging the initial
// multi-node settle out past a few seconds.
const DAMPING = 0.9;
const MAX_SPEED = 14;
const NODE_RADIUS = 28;
// Repulsion (MIN_DIST above) is a force, not a constraint — it just slows an
// approach, it doesn't forbid it, so two nodes can still end up visually
// overlapping (a fast drag flung onto a neighbor, a low Repel slider, or
// springs pulling a connected pair tighter than repulsion pushes back). This
// is the hard floor enforced by the collision-resolution pass in tick(): two
// node circles (radius NODE_RADIUS each) are touching, not overlapping, once
// their centers are this far apart.
const MIN_NODE_CENTER_DIST = NODE_RADIUS * 2;
// Extra Gauss-Seidel-style passes per tick so a chain of overlaps (three or
// more nodes forced close together) converges within one frame — see tick().
const COLLISION_ITERATIONS = 4;
const BOUNDARY_STRENGTH = 0.6;
const VELOCITY_FLOOR = 0.02;
const SETTLE_EPSILON = 0.05;
const SETTLE_FRAMES = 30;
// Was 4000 — bumped modestly alongside the lighter DAMPING above so a
// released node's longer float-and-settle has room to finish via the
// SETTLE_FRAMES low-energy check rather than hitting this hard cutoff
// mid-drift. Don't push this much higher without re-checking the *initial*
// multi-node settle time on page load too, not just a single drag-release —
// they're both governed by the same DAMPING and can drift apart: 0.94 felt
// great for one released node but stretched the full 19-node initial settle
// to ~8s, which is exactly the kind of mismatch this cutoff exists to catch.
const MAX_RUN_MS = 6000;
const CLICK_DRAG_THRESHOLD = 5;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_BUTTON_STEP = 0.15;
const ZOOM_WHEEL_SENSITIVITY = 0.01;

function clampZoom(z) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}
// .stack-graph now starts at literal screen y=0 (see Stack.css — no more
// padding-top pushing it down), so this offset is what actually creates
// clearance below .stack-floating-header — h1 + subheader + StackPhaseNav
// (the phase row + its reserved description line), all rendered by Stack.js
// now, entirely outside this component's own DOM/coordinate system. Tuned by
// measurement, not derived from a DOM read, since this component has no
// visibility into that header's actual rendered height. Sized for the
// *tallest* case (mobile, where the subheader paragraph wraps to 3 lines —
// measured header bottom ~203px at 390px wide, vs. desktop's single-line
// ~179px) since this constant doesn't vary by viewport — the extra margin
// on desktop is harmless, but too little on mobile means nodes drift up
// underneath the phase row/description text.
const HEADER_CLEARANCE_Y = 230;
// Nodes are kept below this line so they never render underneath the
// floating header, even after physics settles into an unpredictable shape.
const HEADER_EXCLUSION_Y = HEADER_CLEARANCE_Y + NODE_RADIUS + 24;

// Sliders default to this percentage of their own min/max range, not a hand-picked value.
const DEFAULT_PERCENT = 77;

// curve > 1 biases percent toward the low end of [min, max] (pct^curve), which
// matters for spring/repulsion: at this graph's edge density, the spring
// force needed to fully snap a connected pair to restLength saturates at a
// small fraction of its own max (repulsion can't out-pull it once spring is
// past ~0.03-0.05), so a *linear* percent mapping spent the first ~20% of the
// slider crossing that saturation point and the remaining 80% looked
// identical — exactly the "no difference between 0 and 100%" complaint. A
// cubic curve stretches that same transition across most of the slider's
// travel instead, so the low/high ends actually read as different. Distance
// (restLength) has no such saturation — it's the equilibrium target
// directly — so it stays linear.
export function valueAtPercent(min, max, pct, curve = 1) {
  return min + Math.pow(pct / 100, curve) * (max - min);
}

export function percentAtValue(min, max, value, curve = 1) {
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return Math.pow(t, 1 / curve) * 100;
}

const SLIDER_RANGES = [
  { key: 'spring', label: 'Pull', min: 0, max: 0.3, curve: 3 },
  { key: 'repulsion', label: 'Repel', min: 0, max: 14000, curve: 3 },
  { key: 'restLength', label: 'Distance', min: 30, max: 400, curve: 1 },
];

// User-adjustable via the settings panel — { key, label, min, max, curve, default }
const SLIDER_CONFIG = SLIDER_RANGES.map((s) => ({
  ...s,
  default: valueAtPercent(s.min, s.max, DEFAULT_PERCENT, s.curve),
}));

const DEFAULT_SPRING = SLIDER_CONFIG.find((s) => s.key === 'spring').default;
const DEFAULT_REPULSION = SLIDER_CONFIG.find((s) => s.key === 'repulsion').default;
const DEFAULT_REST_LENGTH = SLIDER_CONFIG.find((s) => s.key === 'restLength').default;

// Where each phase's node cluster starts (seedNodes only) — spread across the
// canvas width and pushed below the floating header (h1 + subheader +
// StackPhaseNav, rendered by Stack.js now, not this component — see
// HEADER_CLEARANCE_Y), so clusters don't seed underneath it.
function computeSeedAnchors(width) {
  return phaseIds.reduce((acc, id, i) => {
    acc[id] = { x: (width * (i + 1)) / (phaseIds.length + 1), y: HEADER_CLEARANCE_Y };
    return acc;
  }, {});
}

function seedNodes(width, height, seedAnchors) {
  return nodeSeeds.map((seed, i) => {
    const matching = seed.phases.map((p) => seedAnchors[p]).filter(Boolean);
    const centroid = matching.length
      ? {
          x: matching.reduce((sum, p) => sum + p.x, 0) / matching.length,
          y: matching.reduce((sum, p) => sum + p.y, 0) / matching.length,
        }
      : { x: width / 2, y: height / 2 };

    const angle = i * 2.399963;
    const r = 40 + (i % 5) * 14;
    return {
      x: centroid.x + Math.cos(angle) * r,
      y: centroid.y + 160 + Math.sin(angle) * r,
      vx: 0,
      vy: 0,
      fx: 0,
      fy: 0,
      pinned: false,
    };
  });
}

const StackGraph = ({ activePhase, onBackgroundTap }) => {
  const containerRef = useRef(null);
  const nodesRef = useRef([]);
  const nodeElsRef = useRef([]);
  // Edges used to be one <line> per edge (85 of them), each getting 4
  // setAttribute calls every tick — 340 DOM writes/frame just for edges,
  // measured (via CDP CPU throttling + rAF-timed FPS sampling) to cost
  // roughly as much per frame as the nodes' backdrop-filter blur below, and
  // a big contributor to the reported mobile/desktop lag. Batched into two
  // <path> elements instead (one per dimmed-state, since that's the only
  // thing that varies per edge) — same visual result, but each tick only
  // builds two `d` string values and calls setAttribute twice, not 340
  // times. `edgeDimmedRef` is recomputed only when `activePhase` changes
  // (see the effect below), not every frame — dimmed-state doesn't depend
  // on node position, so there's no reason to recheck it 60x/sec.
  const edgeNormalPathRef = useRef(null);
  const edgeDimmedPathRef = useRef(null);
  const edgeDimmedRef = useRef(edgeSeeds.map(() => false));
  const boundsRef = useRef({ width: 0, height: 0 });
  const draggingIndexRef = useRef(-1);
  const activePointerIndexRef = useRef(-1);
  const pointerRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef(null);
  const lastTimeRef = useRef(0);
  const runStartRef = useRef(0);
  const settleCounterRef = useRef(0);
  const reducedMotionRef = useRef(false);
  const paramsRef = useRef({
    spring: DEFAULT_SPRING,
    repulsion: DEFAULT_REPULSION,
    restLength: DEFAULT_REST_LENGTH,
  });
  const zoomRef = useRef(1);
  const viewportRef = useRef(null);
  const panRef = useRef({ x: 0, y: 0 });
  const panDragStartRef = useRef({ x: 0, y: 0 });
  const panOriginRef = useRef({ x: 0, y: 0 });
  // Tracks every currently-down touch pointer (pointerId -> {x, y}), regardless
  // of whether it landed on the background or a node — a two-finger pinch
  // should work either way, matching how canvas apps like Figma treat it.
  // Kept separate from the single-pointer pan/drag refs above since pinch is
  // a distinct gesture that preempts them once a second finger joins.
  const touchPointersRef = useRef(new Map());
  const pinchStartRef = useRef(null);

  // Closed by default on every breakpoint — it's a tucked-away physics/tuning
  // extra, not something that should greet every visitor on page load.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [sliderValues, setSliderValues] = useState(() =>
    SLIDER_CONFIG.reduce((acc, s) => { acc[s.key] = s.default; return acc; }, {})
  );

  // Shared by tick() (called every animation frame, from current physics
  // positions) and the activePhase-change effect below (called once,
  // immediately, from whatever positions nodesRef already holds) — dimmed
  // vs. normal is the only thing that changes when activePhase changes, and
  // without this the visual regrouping would otherwise have to wait for the
  // physics loop to happen to be running rather than applying instantly,
  // the way the old per-line CSS-class approach did for free via React.
  const syncEdgePaths = useCallback(() => {
    const nodes = nodesRef.current;
    const dimmedFlags = edgeDimmedRef.current;
    let normalD = '';
    let dimmedD = '';
    for (let e = 0; e < edgeSeeds.length; e++) {
      const { a, b } = edgeSeeds[e];
      if (!nodes[a] || !nodes[b]) continue;
      const segment = `M${nodes[a].x},${nodes[a].y}L${nodes[b].x},${nodes[b].y}`;
      if (dimmedFlags[e]) dimmedD += segment;
      else normalD += segment;
    }
    if (edgeNormalPathRef.current) edgeNormalPathRef.current.setAttribute('d', normalD);
    if (edgeDimmedPathRef.current) edgeDimmedPathRef.current.setAttribute('d', dimmedD);
  }, []);

  const tick = useCallback((now) => {
    const bounds = boundsRef.current;
    const nodes = nodesRef.current;
    const dt = Math.min((now - (lastTimeRef.current || now)) / 1000, 0.032);
    lastTimeRef.current = now;

    const dragging = draggingIndexRef.current;
    if (dragging !== -1 && nodes[dragging]) {
      // Unbounded while actively dragged — same "let the user take content
      // wherever they want, even off-screen" philosophy as panning
      // (handleWindowPanMove). This used to be clamped to keep the dragged
      // node from going behind the page header, but the header is now
      // pointer-events: none and out of the interactive layer entirely, so
      // there's nothing left for a dragged node to collide with there — the
      // canvas's own `overflow: hidden` just clips whatever's currently
      // outside it, which is fine. Settled (non-dragged) nodes still get
      // clamped further below, so releasing a node lets ambient physics and
      // that boundary pull it back into a sane resting position.
      const prevDragX = nodes[dragging].x;
      const prevDragY = nodes[dragging].y;
      nodes[dragging].x = pointerRef.current.x;
      nodes[dragging].y = pointerRef.current.y;
      // Track the drag's own per-frame velocity (instead of zeroing it)
      // so a fast flick-and-release carries that momentum into free
      // physics on release, rather than the node starting from a dead
      // stop and only ever moving again once forces build back up. This
      // is what actually makes a release feel like a *throw* rather than
      // a drop — the DAMPING/MAX_RUN_MS changes above only control how
      // long that motion (or any other settle motion) takes to die out.
      if (dt > 0) {
        nodes[dragging].vx = (nodes[dragging].x - prevDragX) / (dt * 60);
        nodes[dragging].vy = (nodes[dragging].y - prevDragY) / (dt * 60);
        const dragSpeed = Math.hypot(nodes[dragging].vx, nodes[dragging].vy);
        if (dragSpeed > MAX_SPEED) {
          nodes[dragging].vx = (nodes[dragging].vx / dragSpeed) * MAX_SPEED;
          nodes[dragging].vy = (nodes[dragging].vy / dragSpeed) * MAX_SPEED;
        }
      }
    }

    const isIdle = reducedMotionRef.current && dragging === -1;
    const { spring, repulsion, restLength } = paramsRef.current;

    if (!isIdle) {
      for (let i = 0; i < nodes.length; i++) {
        nodes[i].fx = 0;
        nodes[i].fy = 0;
      }

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const distSq = Math.max(dx * dx + dy * dy, MIN_DIST * MIN_DIST);
          const dist = Math.sqrt(distSq);
          const f = repulsion / distSq;
          const fx = (dx / dist) * f;
          const fy = (dy / dist) * f;
          nodes[i].fx -= fx;
          nodes[i].fy -= fy;
          nodes[j].fx += fx;
          nodes[j].fy += fy;
        }
      }

      for (let e = 0; e < edgeSeeds.length; e++) {
        const { a, b } = edgeSeeds[e];
        const dx = nodes[b].x - nodes[a].x;
        const dy = nodes[b].y - nodes[a].y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 0.0001);
        const diff = dist - restLength;
        const f = spring * diff;
        const fx = (dx / dist) * f;
        const fy = (dy / dist) * f;
        nodes[a].fx += fx;
        nodes[a].fy += fy;
        nodes[b].fx -= fx;
        nodes[b].fy -= fy;
      }

      const centerX = bounds.width / 2;
      const centerY = bounds.height / 2;
      const pad = NODE_RADIUS + 24;

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        node.fx += (centerX - node.x) * CENTER_STRENGTH;
        node.fy += (centerY - node.y) * CENTER_STRENGTH;

        if (node.x < pad) node.fx += (pad - node.x) * BOUNDARY_STRENGTH;
        if (node.x > bounds.width - pad) node.fx -= (node.x - (bounds.width - pad)) * BOUNDARY_STRENGTH;
        if (node.y < pad) node.fy += (pad - node.y) * BOUNDARY_STRENGTH;
        if (node.y > bounds.height - pad) node.fy -= (node.y - (bounds.height - pad)) * BOUNDARY_STRENGTH;
        if (node.y < HEADER_EXCLUSION_Y) node.fy += (HEADER_EXCLUSION_Y - node.y) * BOUNDARY_STRENGTH;
      }

      let totalSpeedSq = 0;
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (i === dragging) continue;
        node.vx = (node.vx + node.fx * dt) * DAMPING;
        node.vy = (node.vy + node.fy * dt) * DAMPING;
        if (Math.abs(node.vx) < VELOCITY_FLOOR) node.vx = 0;
        if (Math.abs(node.vy) < VELOCITY_FLOOR) node.vy = 0;
        const speed = Math.hypot(node.vx, node.vy);
        if (speed > MAX_SPEED) {
          node.vx = (node.vx / speed) * MAX_SPEED;
          node.vy = (node.vy / speed) * MAX_SPEED;
        }
        node.x += node.vx * dt * 60;
        node.y += node.vy * dt * 60;
        // No hard position clamp here (there used to be one) — a node
        // released far outside the canvas (after being dragged there
        // unbounded, see above) eases back on its own via the boundary
        // *force* a few lines up plus spring pull from its neighbors,
        // instead of snapping back instantly on the very next tick. A hard
        // clamp here would undo the whole point of unbounded dragging: the
        // node would teleport back the moment you let go of it.
        totalSpeedSq += node.vx * node.vx + node.vy * node.vy;
      }

      if (totalSpeedSq < SETTLE_EPSILON && dragging === -1) {
        settleCounterRef.current += 1;
      } else {
        settleCounterRef.current = 0;
      }
    }

    // Hard collision resolution — runs every tick regardless of `isIdle`
    // (reduced motion, or a drag in progress) so the no-overlap guarantee
    // holds unconditionally, not just while the force sim is active. A
    // dragged node is treated as immovable here (only the *other* node in an
    // overlapping pair gets pushed) so it keeps tracking the pointer
    // exactly, the same way it's exempt from the boundary clamp above.
    // Resolving a pair can reintroduce a violation with a third node already
    // processed earlier in the same pass (Gauss-Seidel-style, not a true
    // simultaneous solve) — a handful of extra passes converges any such
    // chained overlaps within one frame instead of visibly untangling over
    // several, and is still trivial at 19 nodes.
    for (let pass = 0; pass < COLLISION_ITERATIONS; pass++) {
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.hypot(dx, dy);
          if (dist >= MIN_NODE_CENTER_DIST) continue;
          // Exactly coincident centers have no direction to separate along —
          // fall back to an arbitrary axis rather than dividing by zero.
          const nx = dist > 0.0001 ? dx / dist : 1;
          const ny = dist > 0.0001 ? dy / dist : 0;
          const overlap = MIN_NODE_CENTER_DIST - dist;
          if (i === dragging) {
            b.x += nx * overlap;
            b.y += ny * overlap;
          } else if (j === dragging) {
            a.x -= nx * overlap;
            a.y -= ny * overlap;
          } else {
            a.x -= nx * overlap * 0.5;
            a.y -= ny * overlap * 0.5;
            b.x += nx * overlap * 0.5;
            b.y += ny * overlap * 0.5;
          }
        }
      }
    }

    for (let i = 0; i < nodes.length; i++) {
      const el = nodeElsRef.current[i];
      if (el) el.style.transform = `translate3d(${nodes[i].x - NODE_RADIUS}px, ${nodes[i].y - NODE_RADIUS}px, 0)`;
    }
    syncEdgePaths();

    const ranTooLong = dragging === -1 && now - runStartRef.current > MAX_RUN_MS;

    if (!isIdle && settleCounterRef.current < SETTLE_FRAMES && !ranTooLong) {
      rafIdRef.current = requestAnimationFrame(tick);
    } else {
      rafIdRef.current = null;
      // Node backdrop-filter blur (see .stack-graph-node-hit's shared glass
      // rule in index.css) is disabled while this class is present — see
      // ensureLoopRunning below for why. Once the loop actually stops (here,
      // not just "requested" to), it's safe to restore: nothing is moving
      // underneath the blur anymore, so recomputing it is a one-time cost,
      // not a per-frame one.
      if (containerRef.current) containerRef.current.classList.remove('stack-graph--simulating');
    }
  }, [syncEdgePaths]);

  const ensureLoopRunning = useCallback(() => {
    if (rafIdRef.current == null && nodesRef.current.length > 0) {
      lastTimeRef.current = performance.now();
      runStartRef.current = performance.now();
      settleCounterRef.current = 0;
      rafIdRef.current = requestAnimationFrame(tick);
      // Measured (CDP CPU throttling + rAF-timed FPS sampling) that the 19
      // nodes' backdrop-filter blur — expensive on its own, and *especially*
      // expensive to keep recomputing as the blurred backdrop moves every
      // frame — costs nearly as much per frame as the physics simulation
      // itself while nodes are actually moving (initial settle, an active
      // drag and its connected neighbors). It's free once everything is
      // static, since the browser only has to compute it once. Rather than
      // removing the glass look outright, it's suppressed only for the
      // (comparatively brief, time-bounded) duration the sim is actually
      // animating, via a class read by .stack-graph-node-hit in index.css —
      // the frosted look is unchanged at rest, which is where it's seen the
      // vast majority of the time.
      if (containerRef.current) containerRef.current.classList.add('stack-graph--simulating');
    }
  }, [tick]);

  // The viewport wrapper is visually transformed as translate(pan) scale(zoom)
  // around its own center (transform-origin: center), so converting a screen
  // point into the unscaled/logical coordinates the physics sim runs in means
  // undoing that same pan + center-anchored scale — otherwise dragging drifts
  // from the cursor as soon as you're panned and/or not at 100% zoom.
  const getCanvasPoint = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const visualX = e.clientX - rect.left;
    const visualY = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const z = zoomRef.current;
    const pan = panRef.current;
    return {
      x: cx + (visualX - pan.x - cx) / z,
      y: cy + (visualY - pan.y - cy) / z,
    };
  };

  // Imperative DOM write (not React state) since pan updates fire on every
  // pointermove during a drag — same reasoning as the physics tick's node
  // writes. zoom still gets its own React state too, purely for the % readout.
  const applyTransform = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const { x, y } = panRef.current;
    el.style.transform = `translate(${x}px, ${y}px) scale(${zoomRef.current})`;
  }, []);

  const applyZoom = useCallback((next) => {
    const clamped = clampZoom(next);
    zoomRef.current = clamped;
    setZoom(clamped);
    applyTransform();
  }, [applyTransform]);

  const handleZoomIn = useCallback(() => applyZoom(zoomRef.current + ZOOM_BUTTON_STEP), [applyZoom]);
  const handleZoomOut = useCallback(() => applyZoom(zoomRef.current - ZOOM_BUTTON_STEP), [applyZoom]);
  // Unlike the +/- buttons (which zoom around the viewport's own center,
  // wherever that happens to be relative to the graph), the reset-to-100%
  // button re-centers the pan on the *nodes'* current bounding box — the
  // physics sim's center-strength force keeps nodes roughly centered on
  // `bounds` over time, but "roughly" and "after a resize/drag/settle" can
  // both leave the true node centroid off from bounds' literal center, so a
  // bare `pan = (0,0)` doesn't reliably frame the graph. Recomputed fresh
  // from `nodesRef.current` every click rather than cached, since node
  // positions keep changing (drags, physics settling, window resizes).
  const handleZoomReset = useCallback(() => {
    const nodes = nodesRef.current;
    const bounds = boundsRef.current;
    if (nodes.length && bounds.width && bounds.height) {
      const xs = nodes.map((n) => n.x);
      const ys = nodes.map((n) => n.y);
      const minX = Math.min(...xs) - NODE_RADIUS;
      const maxX = Math.max(...xs) + NODE_RADIUS;
      const minY = Math.min(...ys) - NODE_RADIUS;
      const maxY = Math.max(...ys) + NODE_RADIUS;
      panRef.current = {
        x: bounds.width / 2 - (minX + maxX) / 2,
        y: bounds.height / 2 - (minY + maxY) / 2,
      };
    } else {
      panRef.current = { x: 0, y: 0 };
    }
    applyZoom(1);
  }, [applyZoom]);

  // Panning: click-drag on empty canvas background (not a node) moves the
  // whole viewport. Window-level listeners for the same reason node-dragging
  // uses them — must keep tracking the pointer regardless of what's under it.
  // Deliberately unbounded — the whole point is a free-form canvas you can
  // pan anywhere, including panning content fully off-screen; `.stack-graph`'s
  // `overflow: hidden` just clips whatever's currently outside the viewport,
  // which is the intended behavior here, not a bug to guard against. The
  // zoom Reset button (`handleZoomReset`) is the way back if you pan too far.
  const handleWindowPanMove = useCallback((e) => {
    const dx = e.clientX - panDragStartRef.current.x;
    const dy = e.clientY - panDragStartRef.current.y;
    panRef.current = { x: panOriginRef.current.x + dx, y: panOriginRef.current.y + dy };
    applyTransform();
  }, [applyTransform]);

  // A background pointerup with movement under CLICK_DRAG_THRESHOLD is a tap,
  // not a pan — used to clear the active phase highlight and dismiss the
  // settings panel on tap/click, since touch has no hover equivalent to
  // StackPhaseNav's onMouseLeave and would otherwise leave a phase stuck
  // highlighted with no way to deselect it (the settings panel has an
  // explicit close via its own gear toggle, but closing it on an outside tap
  // too matches how any other dropdown/flyout on the web behaves). Only
  // pointerup counts (not pointercancel, which has no reliable end
  // coordinates to measure against).
  const handleWindowPanUp = useCallback((e) => {
    window.removeEventListener('pointermove', handleWindowPanMove);
    window.removeEventListener('pointerup', handleWindowPanUp);
    window.removeEventListener('pointercancel', handleWindowPanUp);
    document.body.style.userSelect = '';
    setIsPanning(false);

    if (e.type === 'pointerup') {
      const dx = e.clientX - panDragStartRef.current.x;
      const dy = e.clientY - panDragStartRef.current.y;
      if (Math.hypot(dx, dy) <= CLICK_DRAG_THRESHOLD) {
        if (onBackgroundTap) onBackgroundTap();
        setSettingsOpen(false);
      }
    }
  }, [handleWindowPanMove, onBackgroundTap]);

  // Only start a pan when the pointerdown target is the viewport div itself
  // (empty background) — a node's own onPointerDown handles node-dragging,
  // and since it doesn't stop propagation, this would otherwise also fire for
  // every node-drag, fighting the node-drag/click logic.
  const handleBackgroundPointerDown = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    // Panning tracks the pointer via window-level listeners, so the cursor
    // can end up anywhere on the page mid-drag — including over the page
    // header's text. `.stack-graph-viewport` has `user-select: none`, but
    // that only stops a selection from *starting* there; without also
    // preventing the default action and disabling selection page-wide for
    // the duration of the drag, the browser still treats the mouse movement
    // as a text-selection drag once the cursor reaches selectable text
    // elsewhere, highlighting the header title/intro as a side effect.
    e.preventDefault();
    document.body.style.userSelect = 'none';
    panDragStartRef.current = { x: e.clientX, y: e.clientY };
    panOriginRef.current = { ...panRef.current };
    setIsPanning(true);
    window.addEventListener('pointermove', handleWindowPanMove);
    window.addEventListener('pointerup', handleWindowPanUp);
    window.addEventListener('pointercancel', handleWindowPanUp);
  }, [handleWindowPanMove, handleWindowPanUp]);

  // Window-level listeners (rather than element pointer capture) so dragging
  // keeps tracking the pointer no matter what's underneath it, and to avoid
  // spurious pointercancel/lostpointercapture some input pipelines fire when
  // capture is requested mid-gesture.
  const handleWindowPointerMove = useCallback((e) => {
    const i = activePointerIndexRef.current;
    if (i === -1) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const dist = Math.hypot(dx, dy);

    if (draggingIndexRef.current !== i && dist > CLICK_DRAG_THRESHOLD) {
      draggingIndexRef.current = i;
      nodesRef.current[i].pinned = true;
      ensureLoopRunning();
    }

    if (draggingIndexRef.current === i) {
      pointerRef.current = getCanvasPoint(e);
    }
  }, [ensureLoopRunning]);

  const handleWindowPointerUp = useCallback((e) => {
    const i = activePointerIndexRef.current;
    window.removeEventListener('pointermove', handleWindowPointerMove);
    window.removeEventListener('pointerup', handleWindowPointerUp);
    window.removeEventListener('pointercancel', handleWindowPointerUp);
    document.body.style.userSelect = '';
    if (i === -1) return;

    const wasDragging = draggingIndexRef.current === i;
    if (wasDragging) {
      nodesRef.current[i].pinned = false;
      // vx/vy are deliberately left alone here — tick() already tracks the
      // drag's own per-frame velocity every tick (see the dragging branch
      // above), so this is the flick's momentum at the moment of release,
      // not zero. Zeroing it here (as this used to) meant every release
      // started from a dead stop no matter how fast the drag was moving.
      draggingIndexRef.current = -1;
      // MAX_RUN_MS is meant to bound how long the *free-settling* sim can run
      // for, not how long a drag itself lasts — `ranTooLong` only ever checks
      // it while dragging === -1 (tick() skips the check entirely mid-drag).
      // But `runStartRef` is only reset by ensureLoopRunning() when the loop
      // restarts from idle, which for a drag happens once, at the drag's
      // *start* — so a drag held longer than MAX_RUN_MS leaves runStartRef
      // stale by the time it ends, and the very next tick after release sees
      // `now - runStartRef.current` already past the cutoff and stops the
      // loop dead on the spot, instead of letting the release's momentum
      // decay naturally. Resetting it here means the cutoff always measures
      // from when free physics actually begins (release), not from whenever
      // the loop last happened to start.
      runStartRef.current = performance.now();
    } else {
      window.open(nodeSeeds[i].tool.url, '_blank', 'noopener,noreferrer');
    }
    activePointerIndexRef.current = -1;
  }, [handleWindowPointerMove]);

  const handlePointerDown = useCallback((i, e) => {
    // Same reasoning as handleBackgroundPointerDown: node-dragging also
    // tracks the pointer via window-level listeners, so the cursor can end
    // up over unrelated page text mid-drag and trigger a native selection.
    e.preventDefault();
    document.body.style.userSelect = 'none';
    activePointerIndexRef.current = i;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    window.addEventListener('pointermove', handleWindowPointerMove);
    window.addEventListener('pointerup', handleWindowPointerUp);
    window.addEventListener('pointercancel', handleWindowPointerUp);
  }, [handleWindowPointerMove, handleWindowPointerUp]);

  // Tears down whatever single-pointer pan or node-drag might already be in
  // flight from the first finger, so it doesn't keep fighting over the same
  // window-level listeners/state once a second finger turns the gesture into
  // a pinch. Mirrors the cleanup each of those gestures already does on its
  // own pointerup, just triggered early and from one shared place.
  const cancelSinglePointerGesture = useCallback(() => {
    window.removeEventListener('pointermove', handleWindowPanMove);
    window.removeEventListener('pointerup', handleWindowPanUp);
    window.removeEventListener('pointercancel', handleWindowPanUp);
    window.removeEventListener('pointermove', handleWindowPointerMove);
    window.removeEventListener('pointerup', handleWindowPointerUp);
    window.removeEventListener('pointercancel', handleWindowPointerUp);
    setIsPanning(false);
    if (draggingIndexRef.current !== -1) {
      nodesRef.current[draggingIndexRef.current].pinned = false;
      draggingIndexRef.current = -1;
    }
    activePointerIndexRef.current = -1;
  }, [handleWindowPanMove, handleWindowPanUp, handleWindowPointerMove, handleWindowPointerUp]);

  // Pinch-to-zoom, tracked globally (not via the per-element onPointerDown
  // handlers above) because a pinch needs to work regardless of which
  // element each finger happens to land on first — one finger could start on
  // a node, the other on empty background, and it should still zoom rather
  // than starting a node-drag. `touch-action: none` on the viewport/nodes
  // (needed so a single-finger drag doesn't fight the browser's own
  // scroll/zoom) also suppresses the browser's native two-finger pinch, so
  // this is the replacement for it, not an addition alongside it.
  const handleTouchPointerDown = useCallback((e) => {
    if (e.pointerType !== 'touch') return;
    touchPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (touchPointersRef.current.size === 2) {
      cancelSinglePointerGesture();
      document.body.style.userSelect = 'none';
      const [p1, p2] = Array.from(touchPointersRef.current.values());
      pinchStartRef.current = {
        dist: Math.hypot(p2.x - p1.x, p2.y - p1.y),
        zoom: zoomRef.current,
      };
    }
  }, [cancelSinglePointerGesture]);

  const handleTouchPointerMove = useCallback((e) => {
    if (e.pointerType !== 'touch' || !touchPointersRef.current.has(e.pointerId)) return;
    touchPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinchStartRef.current && touchPointersRef.current.size === 2) {
      e.preventDefault();
      const [p1, p2] = Array.from(touchPointersRef.current.values());
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      applyZoom(pinchStartRef.current.zoom * (dist / pinchStartRef.current.dist));
    }
  }, [applyZoom]);

  const handleTouchPointerUp = useCallback((e) => {
    if (e.pointerType !== 'touch') return;
    touchPointersRef.current.delete(e.pointerId);
    if (touchPointersRef.current.size < 2) {
      pinchStartRef.current = null;
      document.body.style.userSelect = '';
    }
  }, []);

  // Ctrl/Cmd+wheel (and trackpad pinch, which browsers report as wheel events
  // with ctrlKey set) zooms; plain wheel is left alone so the page still
  // scrolls normally while the cursor happens to be over the graph. Needs a
  // native listener (not React's onWheel) with { passive: false } since React
  // registers wheel handlers as passive by default, which silently ignores
  // preventDefault and lets the browser's own pinch-to-zoom kick in instead.
  const handleWheel = useCallback((e) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    applyZoom(zoomRef.current - e.deltaY * ZOOM_WHEEL_SENSITIVITY);
  }, [applyZoom]);

  // Recomputed only on the (infrequent) activePhase changes, not every tick
  // — dimmed-state depends purely on which phase is hovered, never on node
  // position, so there's nothing to gain from rechecking it 60x/sec. Calls
  // syncEdgePaths() immediately (rather than waiting for tick() to happen to
  // run next) since the physics loop is idle most of the time the graph is
  // just sitting settled — without this, hovering a phase while idle
  // wouldn't visibly dim/undim anything until some other interaction
  // happened to wake the loop back up.
  useEffect(() => {
    edgeDimmedRef.current = edgeSeeds.map(({ a, b }) => Boolean(activePhase) && !(
      nodeSeeds[a].phases.includes(activePhase) && nodeSeeds[b].phases.includes(activePhase)
    ));
    syncEdgePaths();
  }, [activePhase, syncEdgePaths]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    el.addEventListener('wheel', handleWheel, { passive: false });

    // Pinch is tracked globally (not tied to a single gesture's start/end
    // like the pan/drag listeners above), since it needs to see every touch
    // pointer that goes down anywhere on the graph for the lifetime of the
    // component, not just for the duration of one drag.
    window.addEventListener('pointerdown', handleTouchPointerDown);
    window.addEventListener('pointermove', handleTouchPointerMove, { passive: false });
    window.addEventListener('pointerup', handleTouchPointerUp);
    window.addEventListener('pointercancel', handleTouchPointerUp);

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mq.matches;
    const handleMotionChange = (e) => {
      reducedMotionRef.current = e.matches;
      ensureLoopRunning();
    };
    mq.addEventListener('change', handleMotionChange);

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width === 0 || height === 0) return;
      boundsRef.current = { width, height };
      if (nodesRef.current.length === 0) {
        nodesRef.current = seedNodes(width, height, computeSeedAnchors(width));
      }
      ensureLoopRunning();
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      el.removeEventListener('wheel', handleWheel);
      window.removeEventListener('pointerdown', handleTouchPointerDown);
      window.removeEventListener('pointermove', handleTouchPointerMove);
      window.removeEventListener('pointerup', handleTouchPointerUp);
      window.removeEventListener('pointercancel', handleTouchPointerUp);
      mq.removeEventListener('change', handleMotionChange);
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerUp);
      window.removeEventListener('pointercancel', handleWindowPointerUp);
      window.removeEventListener('pointermove', handleWindowPanMove);
      window.removeEventListener('pointerup', handleWindowPanUp);
      window.removeEventListener('pointercancel', handleWindowPanUp);
      document.body.style.userSelect = '';
    };
  }, [
    ensureLoopRunning,
    handleWindowPointerMove,
    handleWindowPointerUp,
    handleWheel,
    handleWindowPanMove,
    handleWindowPanUp,
    handleTouchPointerDown,
    handleTouchPointerMove,
    handleTouchPointerUp,
  ]);

  const handleSliderChange = useCallback((key, value) => {
    setSliderValues((prev) => ({ ...prev, [key]: value }));
    paramsRef.current[key] = value;
    ensureLoopRunning();
  }, [ensureLoopRunning]);

  const handleResetSliders = useCallback(() => {
    const defaults = SLIDER_CONFIG.reduce((acc, s) => { acc[s.key] = s.default; return acc; }, {});
    setSliderValues(defaults);
    paramsRef.current = { ...paramsRef.current, ...defaults };
    ensureLoopRunning();
  }, [ensureLoopRunning]);

  return (
    <div className="stack-graph" ref={containerRef}>
      <div
        className={`stack-graph-viewport${isPanning ? ' stack-graph-viewport--panning' : ''}`}
        ref={viewportRef}
        onPointerDown={handleBackgroundPointerDown}
      >
        {/* Was one <line> per edge (85 of them) — batched into two <path>s
            (dimmed vs. normal, the only thing that varies per edge) since
            each edge's own position never needs its own DOM node, just its
            segment concatenated into one `d` string. See syncEdgePaths(). */}
        <svg className="stack-graph-edges" aria-hidden="true">
          <path ref={edgeNormalPathRef} className="stack-graph-edge" />
          <path ref={edgeDimmedPathRef} className="stack-graph-edge stack-graph-edge--dimmed" />
        </svg>

        {nodeSeeds.map((seed, i) => (
          <StackGraphNode
            key={seed.tool.name}
            tool={seed.tool}
            nodeRef={(el) => { nodeElsRef.current[i] = el; }}
            dimmed={Boolean(activePhase) && !seed.phases.includes(activePhase)}
            onPointerDown={(e) => handlePointerDown(i, e)}
          />
        ))}
      </div>

      <ul className="sr-only stack-graph-fallback-list">
        {nodeSeeds.map((seed) => (
          <li key={seed.tool.name}>
            <a href={seed.tool.url} target="_blank" rel="noopener noreferrer">{seed.tool.name}</a>
          </li>
        ))}
      </ul>

      {/* Portaled straight to document.body — .stack-page is a `main > div`
          and therefore carries the global `animation: fadeUp ... both` rule,
          whose fill-mode leaves a non-`none` transform sitting on it forever.
          A transformed ancestor becomes the containing block for any
          `position: fixed` descendant, which silently turns "fixed" into
          "fixed to that ancestor's box" instead of the viewport — exactly
          the bug `.stack-floating-header` hit before it was pulled out to a
          top-level sibling. These two can't just move to a JSX sibling the
          same way (`.stack-floating-header` is purely presentational; these
          are wired into this component's own zoom/pan/slider state), so a
          portal buys the same "actually fixed to the viewport" behavior
          without lifting that state out of `StackGraph`. Same pattern
          `Feedback`'s modal already uses for the same underlying reason. */}
      {createPortal(
        <>
          <StackGraphSettings
            sliders={SLIDER_CONFIG}
            values={sliderValues}
            open={settingsOpen}
            onToggle={() => setSettingsOpen((v) => !v)}
            onChange={handleSliderChange}
            onReset={handleResetSliders}
          />

          <StackGraphZoomControls
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleZoomReset}
          />
        </>,
        document.body
      )}
    </div>
  );
};

export default StackGraph;
